/**
 * Images with Text Scroll Section
 * Handles scroll-based image transitions on desktop and swipe on mobile
 */

class ImagesWithTextScroll extends HTMLElement {
  constructor() {
    super();
    this.itemElements = [];
    this.imageElements = [];
    this.textElements = [];
    this.visibleImageElement = null;
    this.isDesktop = false;
    this.scrollHandler = null;
  }

  connectedCallback() {
    console.log('ImagesWithTextScroll connected');
    
    this.itemElements = Array.from(this.querySelectorAll('.images-with-text-scroll__item'));
    this.imageElements = Array.from(this.querySelectorAll('.images-with-text-scroll__image'));
    this.textElements = Array.from(this.querySelectorAll('.images-with-text-scroll__text'));
    
    console.log('Found elements:', {
      items: this.itemElements.length,
      images: this.imageElements.length,
      texts: this.textElements.length
    });

    this.setupBackgroundHandling();
    this.setupAnimationType();
    this.initializeFirstSlide();

    // Remove loading attribute when in view
    this.setupIntersectionObserver();

    // Setup based on screen size
    this.isDesktop = window.matchMedia('(min-width: 1000px)').matches;
    
    if (this.isDesktop) {
      this.setupScrollObservers();
    } else {
      this.setupMobileCarousel();
    }

    // Listen for breakpoint changes
    this.setupBreakpointListener();
  }

  setupIntersectionObserver() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.imageElements.forEach(imageElement => {
            imageElement.removeAttribute('loading');
          });
        }
      });
    });

    observer.observe(this);
  }

  setupScrollObservers() {
    // Handle image transitions on scroll
    this.setupImageScrollTransition();
  }

  setupImageScrollTransition() {
    this.scrollHandler = () => {
      const rect = this.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      console.log('Scroll check:', {
        rectTop: rect.top,
        rectBottom: rect.bottom,
        windowHeight: windowHeight
      });
      
      if (rect.top <= 0 && rect.bottom >= windowHeight) {
        const scrolledInSection = Math.abs(rect.top);
        const sectionScrollableHeight = rect.height - windowHeight;
        const scrollProgress = Math.min(scrolledInSection / sectionScrollableHeight, 1);
        const index = Math.min(
          Math.floor(scrollProgress * this.itemElements.length), 
          this.itemElements.length - 1
        );
        
        console.log('Scroll progress:', {
          scrolledInSection,
          sectionScrollableHeight,
          scrollProgress,
          index
        });
        
        const toImage = this.itemElements[index]?.querySelector('.images-with-text-scroll__image');
        
        if (toImage && toImage !== this.visibleImageElement) {
          console.log('Transitioning to image:', index);
          this.transitionToImage(toImage);
        }
      }
    };

    window.addEventListener('scroll', this.scrollHandler);
    window.addEventListener('wheel', this.scrollHandler);
  }

  transitionToImage(newImage) {
    if (!this.visibleImageElement || !newImage) return;

    console.log('Transitioning from', this.visibleImageElement, 'to', newImage);

    // Get corresponding text elements
    const currentImageIndex = this.imageElements.indexOf(this.visibleImageElement);
    const newImageIndex = this.imageElements.indexOf(newImage);
    
    const currentText = this.textElements[currentImageIndex];
    const newText = this.textElements[newImageIndex];

    // Smooth image transition
    if (this.visibleImageElement) {
      this.visibleImageElement.style.transition = 'opacity 0.6s ease-in-out';
      this.visibleImageElement.style.opacity = '0';
    }
    
    newImage.style.transition = 'opacity 0.6s ease-in-out';
    newImage.style.opacity = '1';
    this.visibleImageElement = newImage;

    // Smooth text transition
    if (currentText && newText && currentText !== newText) {
      // Fade out current text
      currentText.style.transition = 'opacity 0.4s ease-in-out, transform 0.4s ease-in-out';
      currentText.style.opacity = '0';
      currentText.style.transform = 'translateY(10px)';

      // Fade in new text
      setTimeout(() => {
        newText.style.transition = 'opacity 0.6s ease-in-out, transform 0.6s ease-in-out';
        newText.style.opacity = '1';
        newText.style.transform = 'translateY(0)';
      }, 200);
    }
  }

  setupMobileCarousel() {
    // Mobile carousel setup with navigation dots
    console.log('Mobile carousel setup');
    
    // Ensure first slide is properly initialized
    this.initializeFirstSlide();
    
    // Setup navigation dots
    this.setupNavigationDots();
    
    // Setup touch/swipe handling
    this.setupTouchHandling();
  }

  setupNavigationDots() {
    const dotsContainer = this.querySelector('.page-dots');
    if (!dotsContainer) return;

    const dots = dotsContainer.querySelectorAll('.tap-area');
    const items = this.itemElements;

    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
        this.updateDots(index);
      });
    });
  }

  setupTouchHandling() {
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    let isScrolling = false;

    this.addEventListener('touchstart', (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isScrolling = false;
    });

    this.addEventListener('touchmove', (e) => {
      if (!startX || !startY) return;

      currentX = e.touches[0].clientX;
      currentY = e.touches[0].clientY;

      const diffX = Math.abs(startX - currentX);
      const diffY = Math.abs(startY - currentY);

      // Determine if it's a horizontal swipe
      if (diffX > diffY && diffX > 10) {
        isScrolling = true;
        e.preventDefault();
      }
    });

    this.addEventListener('touchend', (e) => {
      if (!isScrolling || !startX || !currentX) return;

      const diffX = startX - currentX;
      const threshold = 50;

      if (Math.abs(diffX) > threshold) {
        const currentIndex = this.getCurrentSlideIndex();
        const totalSlides = this.itemElements.length;

        if (diffX > 0 && currentIndex < totalSlides - 1) {
          // Swipe left - next slide
          this.goToSlide(currentIndex + 1);
          this.updateDots(currentIndex + 1);
        } else if (diffX < 0 && currentIndex > 0) {
          // Swipe right - previous slide
          this.goToSlide(currentIndex - 1);
          this.updateDots(currentIndex - 1);
        }
      }

      startX = 0;
      startY = 0;
      currentX = 0;
      currentY = 0;
      isScrolling = false;
    });
  }

  getCurrentSlideIndex() {
    const selectedItem = this.querySelector('.images-with-text-scroll__item.is-selected');
    return selectedItem ? this.itemElements.indexOf(selectedItem) : 0;
  }

  goToSlide(index) {
    const currentIndex = this.getCurrentSlideIndex();
    const animationType = this.getAttribute('data-animation') || 'fade';
    
    // Remove selected class from all items
    this.itemElements.forEach(item => {
      item.classList.remove('is-selected');
    });

    // Add selected class to current item
    if (this.itemElements[index]) {
      this.itemElements[index].classList.add('is-selected');
    }

    // Update visible image with animation
    const newImage = this.itemElements[index]?.querySelector('.images-with-text-scroll__image');
    if (newImage) {
      this.transitionToImageWithAnimation(newImage, currentIndex, index, animationType);
    }
  }

  transitionToImageWithAnimation(newImage, fromIndex, toIndex, animationType) {
    const currentImage = this.visibleImageElement;
    const currentText = this.textElements[fromIndex];
    const newText = this.textElements[toIndex];

    // Set up animation based on type
    switch(animationType) {
      case 'fade':
        this.applyFadeAnimation(currentImage, newImage, currentText, newText);
        break;
      case 'slide':
        this.applySlideAnimation(currentImage, newImage, currentText, newText, fromIndex, toIndex);
        break;
      case 'scale':
        this.applyScaleAnimation(currentImage, newImage, currentText, newText);
        break;
      case 'flip':
        this.applyFlipAnimation(currentImage, newImage, currentText, newText);
        break;
      case 'slide-up':
        this.applySlideUpAnimation(currentImage, newImage, currentText, newText);
        break;
      default:
        this.applyFadeAnimation(currentImage, newImage, currentText, newText);
    }

    this.visibleImageElement = newImage;
  }

  applyFadeAnimation(currentImage, newImage, currentText, newText) {
    if (currentImage) {
      currentImage.style.transition = 'opacity 0.6s ease-in-out';
      currentImage.style.opacity = '0';
    }
    
    newImage.style.transition = 'opacity 0.6s ease-in-out';
    newImage.style.opacity = '1';

    if (currentText && newText && currentText !== newText) {
      currentText.style.transition = 'opacity 0.4s ease-in-out';
      currentText.style.opacity = '0';
      
      setTimeout(() => {
        newText.style.transition = 'opacity 0.6s ease-in-out';
        newText.style.opacity = '1';
      }, 200);
    }
  }

  applySlideAnimation(currentImage, newImage, currentText, newText, fromIndex, toIndex) {
    const direction = toIndex > fromIndex ? 'left' : 'right';
    
    if (currentImage) {
      currentImage.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
      currentImage.style.transform = `translateX(${direction === 'left' ? '-100%' : '100%'})`;
      currentImage.style.opacity = '0';
    }
    
    newImage.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
    newImage.style.transform = 'translateX(0)';
    newImage.style.opacity = '1';

    if (currentText && newText && currentText !== newText) {
      currentText.style.transition = 'transform 0.4s ease-in-out, opacity 0.4s ease-in-out';
      currentText.style.transform = `translateX(${direction === 'left' ? '-20px' : '20px'})`;
      currentText.style.opacity = '0';
      
      setTimeout(() => {
        newText.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
        newText.style.transform = 'translateX(0)';
        newText.style.opacity = '1';
      }, 200);
    }
  }

  applyScaleAnimation(currentImage, newImage, currentText, newText) {
    if (currentImage) {
      currentImage.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
      currentImage.style.transform = 'scale(0.8)';
      currentImage.style.opacity = '0';
    }
    
    newImage.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
    newImage.style.transform = 'scale(1)';
    newImage.style.opacity = '1';

    if (currentText && newText && currentText !== newText) {
      currentText.style.transition = 'transform 0.4s ease-in-out, opacity 0.4s ease-in-out';
      currentText.style.transform = 'scale(0.9)';
      currentText.style.opacity = '0';
      
      setTimeout(() => {
        newText.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
        newText.style.transform = 'scale(1)';
        newText.style.opacity = '1';
      }, 200);
    }
  }

  applyFlipAnimation(currentImage, newImage, currentText, newText) {
    if (currentImage) {
      currentImage.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
      currentImage.style.transform = 'rotateY(90deg)';
      currentImage.style.opacity = '0';
    }
    
    newImage.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
    newImage.style.transform = 'rotateY(0deg)';
    newImage.style.opacity = '1';

    if (currentText && newText && currentText !== newText) {
      currentText.style.transition = 'transform 0.4s ease-in-out, opacity 0.4s ease-in-out';
      currentText.style.transform = 'rotateY(45deg)';
      currentText.style.opacity = '0';
      
      setTimeout(() => {
        newText.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
        newText.style.transform = 'rotateY(0deg)';
        newText.style.opacity = '1';
      }, 200);
    }
  }

  applySlideUpAnimation(currentImage, newImage, currentText, newText) {
    if (currentImage) {
      currentImage.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
      currentImage.style.transform = 'translateY(-50px)';
      currentImage.style.opacity = '0';
    }
    
    newImage.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
    newImage.style.transform = 'translateY(0)';
    newImage.style.opacity = '1';

    if (currentText && newText && currentText !== newText) {
      currentText.style.transition = 'transform 0.4s ease-in-out, opacity 0.4s ease-in-out';
      currentText.style.transform = 'translateY(-20px)';
      currentText.style.opacity = '0';
      
      setTimeout(() => {
        newText.style.transition = 'transform 0.6s ease-in-out, opacity 0.6s ease-in-out';
        newText.style.transform = 'translateY(0)';
        newText.style.opacity = '1';
      }, 200);
    }
  }

  updateDots(activeIndex) {
    const dots = this.querySelectorAll('.page-dots .tap-area');
    dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    });
  }




  setupBreakpointListener() {
    const mediaQuery = window.matchMedia('(min-width: 1000px)');
    
    const handleBreakpointChange = (event) => {
      this.isDesktop = event.matches;
      
      if (this.isDesktop) {
        // Reset mobile styles and setup desktop
        this.resetMobileStyles();
        this.setupScrollObservers();
        console.log('Switched to desktop mode');
      } else {
        // Cancel desktop animations and setup mobile
        this.cancelDesktopAnimations();
        this.setupMobileCarousel();
        this.initializeFirstSlide();
        console.log('Switched to mobile mode');
      }
    };

    mediaQuery.addEventListener('change', handleBreakpointChange);
  }

  resetMobileStyles() {
    // Reset all image styles
    this.imageElements.forEach((image, index) => {
      image.style.opacity = index === 0 ? '1' : '0';
      image.style.transform = 'none';
      image.style.transition = '';
    });
    
    // Reset all text styles
    this.textElements.forEach((text, index) => {
      text.style.opacity = index === 0 ? '1' : '0';
      text.style.transform = 'none';
      text.style.transition = '';
    });
    
    // Reset item classes
    this.itemElements.forEach((item, index) => {
      item.classList.toggle('is-selected', index === 0);
    });
  }

  cancelDesktopAnimations() {
    // Remove scroll listeners
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('wheel', this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  setupBackgroundHandling() {
    // Get background type from data attribute or default to color
    const backgroundType = this.dataset.backgroundType || 'color';
    
    if (backgroundType === 'image') {
      // For image background, ensure no background color
      this.style.backgroundColor = 'transparent';
      const backgroundImage = this.querySelector('.images-with-text-scroll__image-background');
      if (backgroundImage) {
        backgroundImage.style.display = 'block';
      }
      console.log('Background type: Image - showing background image');
    } else {
      // For color background, ensure no background image
      const backgroundImage = this.querySelector('.images-with-text-scroll__image-background');
      if (backgroundImage) {
        backgroundImage.style.display = 'none';
      }
      console.log('Background type: Color - hiding background image');
    }
  }

  setupAnimationType() {
    // Get animation type from CSS variable
    const animationType = getComputedStyle(this).getPropertyValue('--mobile-animation-type').trim() || 'fade';
    this.setAttribute('data-animation', animationType);
    console.log('Animation type set to:', animationType);
  }

  initializeFirstSlide() {
    // Ensure first slide is visible and properly initialized
    if (this.itemElements.length > 0) {
      // Remove any existing selected classes
      this.itemElements.forEach(item => {
        item.classList.remove('is-selected');
      });
      
      // Set first item as selected
      this.itemElements[0].classList.add('is-selected');
      
      // Set first image as visible
      if (this.imageElements[0]) {
        this.visibleImageElement = this.imageElements[0];
        this.imageElements[0].style.opacity = '1';
        this.imageElements[0].style.transform = 'none';
      }
      
      // Set first text as visible
      if (this.textElements[0]) {
        this.textElements[0].style.opacity = '1';
        this.textElements[0].style.transform = 'none';
      }
      
      // Hide other images
      for (let i = 1; i < this.imageElements.length; i++) {
        this.imageElements[i].style.opacity = '0';
      }
      
      // Hide other texts
      for (let i = 1; i < this.textElements.length; i++) {
        this.textElements[i].style.opacity = '0';
      }
      
      console.log('First slide initialized');
    }
  }

  disconnectedCallback() {
    // Cleanup
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('wheel', this.scrollHandler);
    }
  }
}

// Register custom elements
if (!window.customElements.get('images-with-text-scroll')) {
  window.customElements.define('images-with-text-scroll', ImagesWithTextScroll);
}